/**
 * Which rail actually collects this booking's rent (COL-533).
 *
 * Extracted from TenantPaymentsSection so the rule can be tested directly:
 * it decides what a tenant is told about money leaving their account, and it
 * was wrong in a way no test could see while it lived inline in a component.
 *
 * The rule mirrors `rent-charge-tick`, which is the thing that actually moves
 * the money. The collector debits when, and only when:
 *
 *   get_room_payment_methods(room).sepa_debit_enabled
 *     AND an active `sepa_debit` row in payment_methods for the tenant
 *     AND an active sepa_mandate
 *
 * It never reads `bookings.preferred_payment_method`. The preference records
 * an intent; the mandate is what withdraws money. Where the two disagree, the
 * mandate is the truth and the preference is stale.
 */

export type RentRailInputs = {
  /** `get_room_payment_methods` says this room takes direct debit. */
  sepaEnabled: boolean;
  /** Room/apartment/property takes cards. */
  cardEnabled: boolean;
  /** Rent settles to the owner, never to us. */
  ownerCollects: boolean;
  /** Booking belongs to an external operator — we never collect it. */
  externalOperatorBooking: boolean;
  /** An active `sepa_debit` payment method exists for this tenant. */
  mandateActive: boolean;
  /** `bookings.preferred_payment_method`, free text. */
  preferred: string | null | undefined;
};

export type RentRail = "transfer" | "sepa" | "card" | "none";

export function resolveRentRail(input: RentRailInputs): RentRail {
  /*
    Owner-collected and external-operator bookings are never collected by us,
    whatever the stored preference or the tenant's mandate says. Transfer is
    the only rail that works there, and saying otherwise costs the tenant
    money: they read "collected automatically" and do not transfer rent they
    owe. Checked first so it can never be overridden below.
  */
  if (input.ownerCollects || input.externalOperatorBooking) return "transfer";

  /*
    The mandate wins over the preference, and does not require it to agree.
    A tenant whose booking still reads `bank_transfer` from before they signed
    is being debited; telling them to transfer it themselves makes them pay
    twice. V1 always shows the mandate for this reason.
  */
  if (input.sepaEnabled && input.mandateActive) return "sepa";

  const kind = String(input.preferred ?? "").toLowerCase();
  if (kind.includes("transfer") || kind.includes("bank")) return "transfer";
  if (input.cardEnabled && kind.includes("card")) return "card";
  return "none";
}
